/**
 * iter87.188 — How Rivalry Pro+ works.
 *
 * A single walkthrough sheet the operator opens from the header pill.
 * Explains every element on the /rivalry-pro shell in plain language.
 * Portal-rendered fullscreen mask + centred card, same pattern as
 * ReportIssueModal — closes on backdrop click or ✕.
 *
 * Copy locked in with operator on 2026-02-10 review. If the surface
 * changes shape (new tile / section / badge), this copy MUST be
 * updated to match — the sheet is the user-facing source of truth.
 */
import { createPortal } from "react-dom";
import { X } from "lucide-react";

const SECTIONS = [
    {
        heading: "Rivalry Pro+",
        body:
            "Every crew you run or play in shows up here automatically — as long as an admin has your Pro+ email on file " +
            "(either as the admin of the rivalry, or as a player in its roster).",
    },
    {
        heading: "Your Rivalries",
        body:
            "Crews you administrate. Every rivalry where the admin email matches your Pro+ address lands here on next refresh " +
            "— no invite codes, no linking, nothing to do.",
    },
    {
        heading: "To-do email reminders",
        body:
            "A toggle right under the Your Rivalries header. Turn it on and you'll get one email a day if you have any to-dos outstanding across your rivalries — nothing outstanding, nothing lands in your inbox. Off anytime, same spot.",
    },
    {
        heading: "Rivalries You Play In",
        body:
            "Crews you're a player in but don't run. Auto-surfaces whenever any admin adds your Pro+ email to their roster. Still-active ones list above any that have been closed.",
    },
    {
        heading: "Closed & Archived Rivalries",
        body:
            "A collapsed section below your rivalries (tap it to expand) — not two separate lists, one combined read-only bucket. Closed rivalries stay fully viewable and an admin can hit Reopen from Rivalry Settings anytime. Archived ones are done for good; reopening those needs a site admin.",
    },
    {
        heading: "Start a Rivalry",
        body:
            "Kick off a brand-new crew. You'll be the admin — set the tournament, invite your people, run the show.",
    },
    {
        heading: "Don't see a rivalry that should be here?",
        body:
            "Rivalries are matched to the ONE verified email on your Clerk account — the email you signed in with. Ask the rivalry admin to add that exact email to the participant list. Once they do, the rivalry appears on your next refresh. " +
            "(Adding extra emails to your Clerk account isn't supported — one verified email per Pro+ account, kept simple on purpose.) " +
            "Still not showing up? Tap Report an issue on the floating button (bottom-right) — I'll take a look.",
    },
    {
        heading: "The to-do pill",
        body:
            "The amber pill on each rivalry card. The number is how many things still need your attention — pot picks, champions pick, " +
            "weekly spread picks, invites to send, drafts to start. Tap it to see the full list and jump straight to each one. Nothing outstanding? It turns into a blue \"All caught up\" pill instead — same tap, opens the rivalry. These are the exact same to-dos the daily email reminder (above, under Your Rivalries) checks for. A closed rivalry shows a plain View button instead (nothing left to do); an archived one has no action at all.",
    },
    {
        heading: "Report an issue",
        body:
            "Something broken, missing, or confusing? Tap the floating action button (bottom-right) → Report an issue. Goes straight to the platform admin.",
    },
    {
        heading: "Cancel Pro+",
        body:
            "Ready to step back? Tap the floating action button (bottom-right) → Cancel Pro+. You'll keep full access until the end of your current billing cycle, then drop back to free. Your rivalries stay in the system either way.",
    },
];

export default function HowItWorksSheet({ open, onClose }) {
    if (!open) return null;
    return createPortal(
        <div
            className="fixed inset-0 z-[100] bg-black/80 flex items-start justify-center p-4 overflow-y-auto"
            onClick={(e) => {
                if (e.target === e.currentTarget) onClose?.();
            }}
            data-testid="how-it-works-sheet"
        >
            <div className="brutal-card mt-8 mb-8 w-full max-w-lg p-5 space-y-4">
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="font-display font-black uppercase text-lg" data-testid="how-it-works-title">
                            How Rivalry Pro+ works
                        </h3>
                        <p className="text-xs text-zinc-500 mt-1">
                            A quick tour of what everything on this page does.
                        </p>
                    </div>
                    <button
                        type="button"
                        onClick={onClose}
                        data-testid="how-it-works-close"
                        aria-label="Close"
                        className="text-zinc-400 hover:text-white transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>
                <div className="space-y-4 max-h-[70dvh] overflow-y-auto pr-1">
                    {SECTIONS.map((s, i) => (
                        <div
                            key={s.heading}
                            data-testid={`how-it-works-section-${i}`}
                            className="border-b border-zinc-800 pb-3 last:border-b-0 last:pb-0"
                        >
                            <p className="font-display font-black text-sm uppercase tracking-tight text-cyber">
                                {s.heading}
                            </p>
                            <p className="text-xs text-zinc-300 mt-1 leading-relaxed">{s.body}</p>
                        </div>
                    ))}
                </div>
                <p className="text-[11px] text-zinc-500 text-center pt-2 border-t border-zinc-800">
                    That's it. Tap the pill on any card to dive in.
                </p>
            </div>
        </div>,
        document.body,
    );
}
